import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useSuperAdminRoomsFilter } from '@/features/superAdmin/features/data/rooms/hooks/useSuperAdminRoomsFilter';
import { SuperAdminApartmentListResponseDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type Props = {
    suspendFetching?: boolean;
};

export const useSuperAdminRoomsListApi = ({ suspendFetching }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useSuperAdminRoomsFilter();
    const { take, skip, tenantId, ownerId, searchText, orderBy, orderDirection } = filter;

    const { data, isLoading, error } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.apartments.queryKeys.list,
            take,
            skip,
            tenantId,
            ownerId,
            searchText,
            orderBy,
            orderDirection,
        ],
        queryFn: async (): Promise<SuperAdminApartmentListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.apartments.endpoints.list,
                {
                    skip,
                    take,
                    tenantId: tenantId ?? undefined,
                    ownerId: ownerId ?? undefined,
                    searchText: searchText ?? undefined,
                    orderBy: orderBy ?? undefined,
                    orderDirection,
                },
            );
            const response = await axiosInstance.get<SuperAdminApartmentListResponseDto>(url);
            return response.data;
        },
        enabled: !suspendFetching,
        placeholderData: keepPreviousData,
    });

    return {
        list: data,
        isLoading,
        error,
    };
};
